import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";

interface WaitlistFormProps {
  industry: string;
}

const WaitlistForm = ({ industry }: WaitlistFormProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    const { error } = await supabase.from("waitlist_signups" as any).insert({
      email: email.trim().toLowerCase(),
      name: name.trim() || null,
      industry,
    } as any);

    if (error) {
      if (error.code === "23505") {
        toast.info("You're already on the waitlist for this industry!");
        setSubmitted(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } else {
      toast.success("You've been added to the waitlist!");
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 rounded-xl border border-success/30 bg-success/5">
        <CheckCircle className="h-8 w-8 text-success" />
        <p className="text-foreground font-medium">You're on the list!</p>
        <p className="text-sm text-muted-foreground text-center">
          We'll email you as soon as this industry goes live on SentinelForge.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full max-w-md mx-auto p-6 rounded-xl border border-border bg-card">
      <p className="text-sm text-muted-foreground text-center mb-1">
        Join the waitlist to get notified when this industry launches.
      </p>
      <div>
        <Label className="text-muted-foreground text-xs">Name (optional)</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="bg-secondary border-border text-foreground mt-1"
        />
      </div>
      <div>
        <Label className="text-muted-foreground text-xs">Email *</Label>
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="bg-secondary border-border text-foreground mt-1"
        />
      </div>
      <Button type="submit" disabled={submitting} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Join the Waitlist
      </Button>
    </form>
  );
};

export default WaitlistForm;
